import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import { devoAdotarPlayerId } from './adocaoDePlayerId';
import { DeviceInfo, ScreenOrientation } from '~/services/platform';
import type { IPlatformService } from '~/services/platform/types';
import { logRocketService } from '~/services/logrocket';
import {
  resolveApiBaseUrl,
  resolveTenantSlug,
} from '~/services/embedded/hostConfig';

/**
 * Registration status for the device authentication flow.
 * Authentication is by Device ID (hardware ID) - no API key needed.
 */
export type RegistrationStatus =
  | 'idle' // Initial state, not yet registered
  | 'initializing' // Getting hardware ID and device info
  | 'registering' // Sending registration request to server
  | 'pending_approval' // Waiting for admin approval in backoffice
  | 'approved' // Device approved, can sync via X-Device-Id header
  | 'rejected' // Device rejected by admin
  | 'error'; // Error during registration

/**
 * Response from device registration endpoint.
 * Note: apiKey is deprecated, authentication uses X-Device-Id header now.
 */
interface RegistrationResponse {
  // 'not_found' = server retornou 200 mas device não existe (alternativa a 404).
  // Defensive — server hoje retorna 404 no GET /status/{id}, mas em outros
  // endpoints (registerDevice fallback) o status="not_found" é usado.
  status: 'pending' | 'approved' | 'rejected' | 'not_found';
  playerId?: string;
  apiKey?: string; // Deprecated - kept for backward compatibility
  rejectionReason?: string;
  message?: string;
}

const POLLING_INTERVAL_MS = 5000; // Poll every 5 seconds for approval
const REGISTRATION_ENDPOINT = '/api/devices/register';
const STATUS_CHECK_ENDPOINT = '/api/devices/status';
const MIN_REGISTRATION_INTERVAL_MS = 2000; // Minimum 2 seconds between registration attempts
const ORIENTATION_STORAGE_KEY = 'efflux_selected_orientation';
const BATTERY_LEVEL_STORAGE_KEY = 'efflux_device_battery_level';
const IS_CHARGING_STORAGE_KEY = 'efflux_device_is_charging';

/**
 * Cooldown entre recoveries de revocation. Evita storm caso múltiplos
 * serviços disparem 401 simultâneo (sse-token + sync + analytics). Renato
 * 2026-06-12: 2 min é conservador — recovery legítimo gera 1 disparo,
 * false-positives transitórios são absorvidos.
 */
const REVOCATION_COOLDOWN_MS = 2 * 60 * 1000;

class AuthStore {
  // Device identification
  deviceId: string = '';
  deviceInfo: DeviceInfo | null = null;

  // Authentication state
  // Note: Authentication is by Device ID (X-Device-Id header), no API key needed
  registrationStatus: RegistrationStatus = 'idle';
  playerId: string | null = null;
  isAuthenticated: boolean = false;

  // Orientation (for manual override in registration)
  selectedOrientation: ScreenOrientation | null = null;

  // Error handling
  error: string | null = null;
  rejectionReason: string | null = null;

  // Internal
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private platformService: IPlatformService | null = null; // Will be injected
  private sseConnection: EventSource | null = null;
  private lastRegistrationAttempt: number = 0; // Timestamp to prevent rapid re-registration
  private lastRevocationHandle: number = 0; // Timestamp do último handleServerRevocation (cooldown)

  constructor() {
    makeAutoObservable(this);
    this.checkExistingAuth();
    this.loadSelectedOrientation();
  }

  /**
   * Adota o UUID real do player (`tab_players.id`) que o servidor já resolveu,
   * quando o fluxo de registro não chegou a preenchê-lo.
   *
   * **Por que existe:** o player EMBEDDED autentica por device token e nunca
   * passa pelo registro/aprovação que grava `efflux_player_id`. Resultado:
   * `playerId` fica `null` para sempre (medido com ~4h de uptime — não é a race
   * de boot que o ConfigManager supõe), e dois caminhos ficam travados:
   *
   * - `syncStore.sync()` sai no guard `(!deviceId || !playerId)` — inclusive na
   *   volta do background, onde o SSE reconecta mas o sync não roda;
   * - o `ConfigManager` não ackeia config (precisa de um `ackPlayerId`).
   *
   * O `hello` do canal SSE já traz esse UUID resolvido pelo servidor — ele era
   * apenas logado. Aqui passa a ser aproveitado.
   *
   * ⚠️ **NÃO mexe em `isAuthenticated`, de propósito.** A reaction de
   * `sync.store` dispara `initializeFromCache()` + `sync()` quando ele vira
   * true, e esse caminho já teve race com o `mediaStore.init()` (fix
   * 2026-06-12, "buffering eterno offline"). Os dois guards acima pedem apenas
   * o `playerId` — o do ACK inclusive diz "even if verification is pending".
   *
   * Inerte na frota: Android/Tizen já têm `playerId` do registro e saem na
   * primeira linha.
   */
  adotarPlayerIdResolvido(uuid: string | null | undefined): void {
    const anterior = this.playerId;
    if (!devoAdotarPlayerId(anterior, uuid)) return;
    // `devoAdotarPlayerId` já garantiu UUID canônico (nunca external_id).
    const resolvido = uuid as string;

    if (anterior) {
      // Divergência: o guardado está errado. Antes isto era um `return` — a regra
      // que protegia a frota impedia o conserto, e um id fantasma (deletado no
      // servidor) sobrevivia a todos os boots. Vide `devoAdotarPlayerId`.
      console.warn(
        `[AuthStore] 🆔 playerId guardado (${anterior}) diverge do que o servidor resolveu (${resolvido}) — adotando o do servidor`,
      );
    } else {
      console.log(
        '[AuthStore] 🆔 playerId adotado do hello do SSE (registro não resolveu):',
        resolvido,
      );
    }
    this.playerId = resolvido;
    try {
      // Persiste pra valer já no próximo boot, sem depender do SSE.
      localStorage.setItem('efflux_player_id', resolvido);
    } catch {
      // Storage indisponível (modo privado/quota): o valor em memória serve
      // para esta sessão e o próximo boot tenta de novo pelo hello.
    }
  }

  /**
   * Check if device is already authenticated from previous session.
   * Authentication is by Device ID - if device was approved before, it's still valid.
   *
   * IMPORTANT: Only mark as authenticated if playerId is NOT empty.
   * An empty playerId means the device is still pending approval.
   */
  private checkExistingAuth(): void {
    // Check for existing auth
    let storedDeviceId = localStorage.getItem('efflux_device_id');
    const storedPlayerId = localStorage.getItem('efflux_player_id');

    // PURGE TEMPORARY IDs
    // If we find a temporary ID from a previous session/version, wipe it.
    if (
      storedDeviceId &&
      (storedDeviceId.startsWith('efflux-temp-') ||
        storedDeviceId.startsWith('DEV-'))
    ) {
      console.log(
        '[AuthStore] 🧹 Purging temporary/dev ID from storage:',
        storedDeviceId,
      );
      localStorage.removeItem('efflux_device_id');
      storedDeviceId = null;
    }

    console.log('[AuthStore] Checking existing auth:', {
      hasDeviceId: !!storedDeviceId,
      hasPlayerId: !!storedPlayerId,
      playerIdIsEmpty: storedPlayerId === '',
      deviceId: storedDeviceId?.substring(0, 20) + '...',
      playerId: storedPlayerId?.substring(0, 8) + '...',
    });

    const wasApproved =
      localStorage.getItem('efflux_device_approved') === 'true';

    // If device was previously approved (with or without playerId), verify with server
    if (
      storedDeviceId &&
      (wasApproved || (storedPlayerId && storedPlayerId !== ''))
    ) {
      console.log(
        '[AuthStore] ✅ Found existing authentication in localStorage - will verify with server on init',
      );
      this.deviceId = storedDeviceId;
      this.playerId = storedPlayerId || null;
      // Don't mark as authenticated yet - will verify with server in initializeWithPlatform
      // This prevents sync attempts if the device was rejected/deleted on server
      this.isAuthenticated = false;
      this.registrationStatus = 'idle'; // Will be verified when initializeWithPlatform is called
    } else if (storedDeviceId && storedPlayerId === '') {
      // Device was registered but never approved
      console.log(
        '[AuthStore] ⚠️ Device was registered but not yet approved - pending approval',
      );
      this.deviceId = storedDeviceId;
      this.playerId = null;
      this.isAuthenticated = false;
      this.registrationStatus = 'pending_approval';
      // Don't clear localStorage - keep deviceId so we can resume polling
    } else {
      console.log(
        '[AuthStore] No existing authentication found, will need to register',
      );
    }

    // Cleanup deprecated API key storage
    localStorage.removeItem('efflux_device_api_key');
  }

  private loadSelectedOrientation(): void {
    const storedOrientation = localStorage.getItem(ORIENTATION_STORAGE_KEY);
    if (storedOrientation === 'landscape' || storedOrientation === 'portrait') {
      this.selectedOrientation = storedOrientation;
    }
  }

  /**
   * Initialize device identification only (without registration flow).
   * This populates deviceId and deviceInfo without triggering registration.
   * On native platforms (Android, Tizen, webOS), always generates the hardware ID
   * and updates if it differs from localStorage (fixes device ID mismatch issues).
   */
  async initializeDeviceId(platformService: IPlatformService): Promise<void> {
    try {
      const hardwareId = await platformService.getHardwareId();
      const deviceInfo = await platformService.getDeviceInfo();
      const storedDeviceId = localStorage.getItem('efflux_device_id');

      // If an ID already exists, keep it to avoid unexpected device churn.
      if (storedDeviceId) {
        runInAction(() => {
          this.deviceId = storedDeviceId;
          this.deviceInfo = deviceInfo;
        });

        this.persistBatteryInfo(deviceInfo);

        console.log(
          '[AuthStore] Using existing device ID from localStorage:',
          storedDeviceId.substring(0, 20) + '...',
        );
        return;
      }

      runInAction(() => {
        this.deviceId = hardwareId;
        this.deviceInfo = deviceInfo;
      });

      this.persistBatteryInfo(deviceInfo);

      // Persist to localStorage for future sessions
      localStorage.setItem('efflux_device_id', hardwareId);

      console.log(
        '[AuthStore] Device ID initialized:',
        hardwareId.substring(0, 20) + '...',
      );
    } catch (error) {
      console.error('[AuthStore] Failed to initialize device ID:', error);
    }
  }

  /**
   * Verify that the stored device ID matches the actual hardware ID.
   * If different, update localStorage and authStore with the correct hardware ID.
   * This handles cases where localStorage was set from a different device/env.
   */
  private async verifyAndUpdateHardwareId(
    platformService: IPlatformService,
  ): Promise<void> {
    try {
      const hardwareId = await platformService.getHardwareId();
      const storedDeviceId = localStorage.getItem('efflux_device_id');

      if (storedDeviceId && storedDeviceId !== hardwareId) {
        console.log(
          '[AuthStore] ⚠️ Hardware ID mismatch detected - updating to correct ID',
          {
            stored: storedDeviceId.substring(0, 20) + '...',
            actual: hardwareId.substring(0, 20) + '...',
          },
        );

        runInAction(() => {
          this.deviceId = hardwareId;
          // INVALIDATE SESSION: ID changed, so previous player association is invalid/suspect
          this.playerId = null;
          this.isAuthenticated = false;
          this.registrationStatus = 'idle';
        });

        // Update localStorage
        localStorage.setItem('efflux_device_id', hardwareId);
        localStorage.removeItem('efflux_player_id'); // Clear potentially stale player ID

        console.log(
          '[AuthStore] ✅ Device ID corrected to hardware ID. Session invalidated to force re-registration.',
          hardwareId.substring(0, 20) + '...',
        );
      }
    } catch (error) {
      console.error('[AuthStore] Failed to verify hardware ID:', error);
    }
  }

  /**
   * Initialize the authentication flow with a platform service.
   * This should be called once when the app starts.
   */
  async initializeWithPlatform(
    platformService: IPlatformService,
  ): Promise<void> {
    // Demo mode: skip all registration
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      runInAction(() => {
        this.deviceId = 'demo-device-001';
        this.isAuthenticated = true;
        this.registrationStatus = 'approved';
      });
      return;
    }

    this.platformService = platformService;

    // If deviceId and playerId exist in localStorage but status is 'idle', verify with server first
    const storedDeviceId = localStorage.getItem('efflux_device_id');
    const storedPlayerId = localStorage.getItem('efflux_player_id');

    if (
      storedDeviceId &&
      storedPlayerId &&
      storedPlayerId !== '' &&
      this.registrationStatus === 'idle'
    ) {
      console.log(
        '[AuthStore] Found stored auth, verifying with server before marking as authenticated',
      );
      this.deviceId = storedDeviceId;
      this.playerId = storedPlayerId;

      // Verify status with server
      try {
        const contentApiUrl = resolveApiBaseUrl();
        const response = await axios.get<RegistrationResponse>(
          `${contentApiUrl}${STATUS_CHECK_ENDPOINT}/${storedDeviceId}`,
          { timeout: 5000 },
        );

        if (response.data.status === 'approved') {
          console.log('[AuthStore] ✅ Server confirms device is approved');

          // Verify hardware ID matches stored ID - update if different
          // This fixes device ID mismatch when localStorage has old value
          // NOTE: If this updates the ID, it might clear the session state.
          // We must ensure we re-establish the authenticated state with the correct PROVEN playerId from server.
          await this.verifyAndUpdateHardwareId(platformService);

          runInAction(() => {
            this.isAuthenticated = true;
            this.registrationStatus = 'approved';
            // CRITICAL FIX: Ensure playerId is set from server response or falls back to stored
            // This handles the case where verifyAndUpdateHardwareId() might have cleared it.
            this.playerId = response.data.playerId || storedPlayerId;
          });
          // O servidor acabou de CONFIRMAR a aprovação — grava o marcador durável.
          // Sem isto, este caminho deixava o device aprovado só em memória: no
          // próximo boot SEM rede, `wasApprovedBefore` era false, o modo offline
          // não entrava, e o player ia pro "Aguardando conteúdo" com o cache
          // inteiro em disco. Vide `persistirAprovacao`.
          this.persistirAprovacao(this.playerId);
          return; // Already authenticated, no need to initialize
        } else {
          console.log(
            '[AuthStore] ⚠️ Server says device is not approved, status:',
            response.data.status,
          );
          // Device is pending or rejected - clear playerId and re-register
          localStorage.removeItem('efflux_player_id');
          this.playerId = null;
          this.isAuthenticated = false;
          if (response.data.status === 'pending') {
            this.registrationStatus = 'pending_approval';
            this.startPollingForApproval();
            return;
          } else {
            this.registrationStatus = 'idle';
          }
        }
      } catch (error: unknown) {
        // If 404, device not found - clear and re-register
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          console.log(
            '[AuthStore] ⚠️ Device not found on server (404), clearing localStorage and re-registering',
          );
          localStorage.removeItem('efflux_player_id');
          this.playerId = null;
          this.isAuthenticated = false;
          this.registrationStatus = 'idle';
        } else {
          // 2026-06-12 (Renato): distinguir Network Error (server inalcançável,
          // tablet offline) de outros erros (5xx, timeout do server, etc).
          //
          // Cenário do bug: tablet rebootou SEM internet, tem cache válido
          // (efflux_cached_campaigns + 84.7MB de vídeos no filesystem) +
          // localStorage marca efflux_device_approved=true. Antes desse fix,
          // qualquer erro → registrationStatus='idle' → Auth scene NÃO
          // navega pro Stream → tela preta apesar de ter tudo pra tocar.
          //
          // Fix: se o erro é claramente de rede (offline) E o device JÁ
          // estava aprovado antes, confiar no localStorage. Player toca do
          // cache. Próximo sync vai re-validar quando a internet voltar,
          // e o caminho do 404 já recupera caso o device tenha sumido.
          const isNetworkError =
            axios.isAxiosError(error) &&
            (error.code === 'ERR_NETWORK' ||
              error.code === 'ECONNABORTED' ||
              error.code === 'ETIMEDOUT' ||
              !error.response); // sem response = não chegou no server

          const wasApprovedBefore =
            localStorage.getItem('efflux_device_approved') === 'true';

          if (isNetworkError && wasApprovedBefore && storedPlayerId) {
            console.log(
              '[AuthStore] 📡 Network error verificando status, mas device já era aprovado — entrando em MODO OFFLINE com cache local',
              {
                errorCode: axios.isAxiosError(error) ? error.code : 'unknown',
                hasStoredPlayerId: true,
                cacheAvailable: !!localStorage.getItem(
                  'efflux_cached_campaigns',
                ),
              },
            );
            // Confia no localStorage — player toca do cache até server voltar.
            // Os syncs subsequentes (a cada 5min via polling fallback)
            // tentam re-validar. Se server responder e device for inválido
            // (404), o recoverFromDeletedDevice já cobre o recovery.
            runInAction(() => {
              this.isAuthenticated = true;
              this.registrationStatus = 'approved';
              this.playerId = storedPlayerId;
            });
            return; // pula o resto da initialization — já tá pronto pra rodar offline
          }

          // Outros erros (5xx, timeout pós-conexão, etc): conservador.
          // Device pode estar OK no server mas algo errado — não marca
          // como authenticated. Próxima tentativa vai resolver.
          console.warn(
            '[AuthStore] Failed to verify status, will re-register to be safe',
            error instanceof Error ? error.message : error,
          );
          this.isAuthenticated = false;
          this.registrationStatus = 'idle';
        }
      }
    }

    // If already authenticated (after verification), no need to re-initialize
    if (this.isAuthenticated) {
      console.log('[AuthStore] Already authenticated, skipping initialization');
      return;
    }

    // If device is pending approval, resume polling/SSE to detect approval
    if (this.registrationStatus === 'pending_approval' && this.deviceId) {
      console.log(
        '[AuthStore] Device is pending approval, resuming polling/SSE',
      );
      this.startPollingForApproval();
      return;
    }

    await this.initializeDevice();
  }

  /**
   * Get hardware ID and device info, then start registration flow.
   */
  async initializeDevice(): Promise<void> {
    if (!this.platformService) {
      console.error('[AuthStore] Platform service not set');
      runInAction(() => {
        this.registrationStatus = 'error';
        this.error = 'Platform service not initialized';
      });
      return;
    }

    runInAction(() => {
      this.registrationStatus = 'initializing';
      this.error = null;
    });

    try {
      // Get hardware ID from platform
      const hardwareId = await this.platformService.getHardwareId();
      const deviceInfo = await this.platformService.getDeviceInfo();

      runInAction(() => {
        this.deviceId = hardwareId;
        this.deviceInfo = deviceInfo;
      });

      this.persistBatteryInfo(deviceInfo);

      console.log('[AuthStore] Device initialized:', {
        deviceId: hardwareId,
        platform: deviceInfo.platform,
        model: deviceInfo.model,
      });

      // Start registration
      await this.registerDevice();
    } catch (error: unknown) {
      console.error('[AuthStore] Failed to initialize device:', error);
      runInAction(() => {
        this.registrationStatus = 'error';
        this.error =
          (error instanceof Error ? error.message : null) ||
          'Failed to initialize device';
      });
    }
  }

  private persistBatteryInfo(deviceInfo: DeviceInfo | null): void {
    const battery = deviceInfo?.battery;
    if (
      battery &&
      typeof battery.level === 'number' &&
      typeof battery.isCharging === 'boolean'
    ) {
      localStorage.setItem(BATTERY_LEVEL_STORAGE_KEY, String(battery.level));
      localStorage.setItem(IS_CHARGING_STORAGE_KEY, String(battery.isCharging));
      return;
    }

    localStorage.setItem(BATTERY_LEVEL_STORAGE_KEY, 'NA');
    localStorage.setItem(IS_CHARGING_STORAGE_KEY, 'NA');
  }

  /**
   * Register device with the server.
   */
  async registerDevice(): Promise<void> {
    if (!this.deviceId || !this.deviceInfo) {
      console.error('[AuthStore] Device not initialized');
      return;
    }

    // Prevent rapid re-registration (de-duplication)
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastRegistrationAttempt;
    if (timeSinceLastAttempt < MIN_REGISTRATION_INTERVAL_MS) {
      console.warn(
        `[AuthStore] Registration attempt blocked - too soon (${timeSinceLastAttempt}ms since last attempt)`,
      );
      return;
    }
    this.lastRegistrationAttempt = now;

    runInAction(() => {
      this.registrationStatus = 'registering';
      this.error = null;
    });

    const contentApiUrl = resolveApiBaseUrl();
    const tenantId = import.meta.env.VITE_TENANT_ID;
    // Renato 2026-06-13: fallback 'default' caía no tenant Platform Builders
    // em prod (player web sem VITE_TENANT_SLUG no build acabava cadastrado em
    // tenant errado). Defaultamos pra 'efflux' — tenant oficial de produção.
    // Builds APK/Tizen têm VITE_TENANT_SLUG hardcoded → não dependem disso.
    // Quando Fase 2 entrar (slug na URL), esse fallback é o último recurso.
    const tenantSlug = resolveTenantSlug();

    try {
      console.log('[AuthStore] Registering device:', {
        deviceId: this.deviceId,
        tenantId,
        tenantSlug,
      });

      const response = await axios.post<RegistrationResponse>(
        `${contentApiUrl}${REGISTRATION_ENDPOINT}`,
        {
          hardwareId: this.deviceId,
          tenantId,
          tenantSlug,
          deviceInfo: {
            ...this.deviceInfo,
            orientation: this.getEffectiveOrientation(),
          },
        },
        {
          timeout: 15000,
          headers: { 'Content-Type': 'application/json' },
        },
      );

      const data = response.data;
      console.log('[AuthStore] Registration response:', data);

      runInAction(() => {
        if (data.status === 'approved') {
          this.handleApproval(data.playerId);
        } else if (data.status === 'rejected') {
          this.registrationStatus = 'rejected';
          this.rejectionReason = data.rejectionReason || 'Device rejected';
          // Keep polling/SSE active even when rejected, so we can detect when admin approves it
          this.startPollingForApproval();
        } else {
          // status === 'pending'
          this.registrationStatus = 'pending_approval';
          this.playerId = data.playerId || null;
          // Start polling or SSE for approval
          this.startPollingForApproval();
        }
      });
    } catch (error: unknown) {
      console.error('[AuthStore] Registration failed:', error);

      // Check if it's a 404 (endpoint not implemented yet)
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        runInAction(() => {
          this.registrationStatus = 'pending_approval';
          this.error =
            'Registration endpoint not available. Please implement /api/devices/register';
        });
        // Start polling anyway for testing
        this.startPollingForApproval();
        return;
      }

      logRocketService.trackError(
        error instanceof Error ? error : new Error(String(error)),
        {
          action: 'device_registration',
          deviceId: this.deviceId,
        },
      );
      runInAction(() => {
        this.registrationStatus = 'error';
        this.error =
          (error instanceof Error ? error.message : null) ||
          'Registration failed';
      });
    }
  }

  /**
   * Handle device approval - save state and persist.
   * Authentication is by Device ID (X-Device-Id header), no API key needed.
   */
  private handleApproval(playerId?: string): void {
    console.log(
      '[AuthStore] 🎉 Device approved! Starting approval handling...',
      {
        playerId: playerId || '(none)',
        deviceId: this.deviceId,
        currentIsAuthenticated: this.isAuthenticated,
        currentStatus: this.registrationStatus,
      },
    );

    // Use runInAction to ensure MobX properly tracks these state changes
    runInAction(() => {
      this.playerId = playerId || null;
      this.isAuthenticated = true;
      this.registrationStatus = 'approved';

      console.log('[AuthStore] ✅ State updated in runInAction:', {
        isAuthenticated: this.isAuthenticated,
        registrationStatus: this.registrationStatus,
        playerId: this.playerId,
        deviceId: this.deviceId,
      });
    });

    // Track device approval in LogRocket
    logRocketService.track('device_approved', {
      deviceId: this.deviceId,
      playerId: playerId || 'none',
    });

    // Persist to localStorage (device ID and player ID)
    // Store 'approved' flag so we know device was approved even without playerId
    this.persistirAprovacao(playerId);

    console.log('[AuthStore] 💾 Persisted auth to localStorage:', {
      deviceId: this.deviceId,
      playerId: playerId || '(empty)',
      verifyIsAuthenticated: this.isAuthenticated,
      verifyRegistrationStatus: this.registrationStatus,
    });

    this.stopPolling();

    // Force a small delay to ensure MobX reactivity propagates
    // This helps ensure the useEffect in Auth component picks up the change
    setTimeout(() => {
      console.log('[AuthStore] 🔍 Post-approval check (after 50ms):', {
        isAuthenticated: this.isAuthenticated,
        registrationStatus: this.registrationStatus,
        deviceId: this.deviceId,
        playerId: this.playerId,
      });

      // Verify localStorage was saved correctly
      const storedDeviceId = localStorage.getItem('efflux_device_id');
      const storedPlayerId = localStorage.getItem('efflux_player_id');
      console.log('[AuthStore] 🔍 localStorage verification:', {
        storedDeviceId,
        storedPlayerId,
        matches: storedDeviceId === this.deviceId,
      });

      // 🆕 CRITICAL FIX: Trigger sync retry after approval
      // This ensures sync executes immediately after device is approved
      setTimeout(() => {
        console.log(
          '[AuthStore] 🔄 Device approved - triggering sync retry...',
        );

        // Import syncStore dynamically to avoid circular dependency
        import('~/stores')
          .then(({ stores }) => {
            if (stores.syncStore) {
              console.log(
                '[AuthStore] ✅ Calling syncStore.sync() after approval',
              );
              stores.syncStore.sync();
            } else {
              console.warn('[AuthStore] ⚠️ syncStore not available');
            }
          })
          .catch((error) => {
            console.error('[AuthStore] ❌ Error importing syncStore:', error);
          });
      }, 500); // Wait 500ms for state propagation
    }, 50);
  }

  /**
   * Start polling the server for approval status.
   * This is called for pending, rejected, or any status that needs monitoring.
   */
  private startPollingForApproval(): void {
    // Don't stop polling if already running - just ensure SSE is connected
    if (this.pollingInterval) {
      console.log(
        '[AuthStore] Polling already active, ensuring SSE connection...',
      );
      this.connectToSSE(); // Ensure SSE is connected
      return;
    }

    console.log('[AuthStore] Starting approval polling...');

    this.pollingInterval = setInterval(() => {
      this.checkApprovalStatus();
    }, POLLING_INTERVAL_MS);

    // Also try to connect via SSE for real-time updates
    this.connectToSSE();
  }

  /**
   * Check approval status via polling.
   * Authentication is by Device ID - no API key needed.
   * Also checks when device is rejected, in case admin approves it later.
   */
  private async checkApprovalStatus(): Promise<void> {
    // Continue polling for both pending and rejected statuses
    // (rejected devices can be approved later by admin)
    if (
      this.registrationStatus !== 'pending_approval' &&
      this.registrationStatus !== 'rejected'
    ) {
      this.stopPolling();
      return;
    }

    const contentApiUrl = resolveApiBaseUrl();

    try {
      const response = await axios.get<RegistrationResponse>(
        `${contentApiUrl}${STATUS_CHECK_ENDPOINT}/${this.deviceId}`,
        { timeout: 10000 },
      );

      const data = response.data;

      runInAction(() => {
        if (data.status === 'approved') {
          // Device approved - authentication uses X-Device-Id header
          this.handleApproval(data.playerId);
        } else if (data.status === 'rejected') {
          this.registrationStatus = 'rejected';
          this.rejectionReason = data.rejectionReason || 'Device rejected';
          // Keep polling active even when rejected, so we can detect when admin approves it
          // Don't stop polling here - continue checking for status changes
        } else if (data.status === 'not_found') {
          // Defensive: server pode retornar 200 com status=not_found em
          // vez de 404 (depende do path). Mesmo tratamento do 404 abaixo.
          this.recoverFromDeletedDevice('status=not_found');
        }
        // If still pending, continue polling
      });
    } catch (error) {
      // 404 = device foi DELETADO no admin (não é só blocked). Player
      // local fica preso em rejected pra sempre porque nunca recupera
      // o estado. Mesma lógica do startup (initializeWithPlatform): limpa
      // localStorage + reset pra 'idle' → próximo tick dispara re-register
      // automático. Without this fix, o player precisaria de reinstall pra voltar.
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.log(
          '[AuthStore] ⚠️ Device not found on server during polling (404) — admin deleted it. Clearing localStorage and re-registering.',
        );
        runInAction(() => {
          this.recoverFromDeletedDevice('HTTP 404');
        });
        return;
      }
      // Outros erros — silently ignore, polling vai tentar de novo.
      console.debug('[AuthStore] Polling check failed, will retry...');
    }
  }

  /**
   * Wrapper público pra `recoverFromDeletedDevice` — usado pelo response
   * interceptor do axios quando GET/POST em `/api/players/{playerId}` ou
   * `/api/config/player/{playerId}` retorna 404. Indica que o playerId cached
   * no localStorage referencia um player que não existe mais no banco
   * (deletado pelo admin OU re-registrado com novo UUID). Sem isso, o cliente
   * ficava em loop chamando endpoints inválidos e nunca disparava sync —
   * tela ficava em "Aguardando conteúdo" indefinidamente até clear manual.
   *
   * Idempotente: chamadas múltiplas no curto prazo só logam a 1ª e mantêm
   * o `registrationStatus` em 'idle' (re-registro já foi disparado).
   */
  public handleStalePlayerId(reason: string): void {
    // Idempotência — se já estamos em recovery, ignora chamadas extras
    if (this.registrationStatus === 'idle' && !this.playerId) {
      return;
    }
    runInAction(() => {
      this.recoverFromDeletedDevice(reason);
    });
  }

  /**
   * Recovery comum pra quando o device foi deletado no admin (404 no status
   * endpoint OU status="not_found"). Limpa localStorage do player e volta
   * pra 'idle' — o fluxo de auto-re-registro do `Auth` scene pega a partir
   * daí, faz POST /api/devices/register e cria um device novo no banco com
   * o mesmo deviceId (hardwareId). Estado anterior (rejected/blocked) é
   * descartado porque já não existe nada pra preservar — registro novo.
   *
   * Não chama `runInAction` internamente — chamadores devem envolver.
   */
  /**
   * Grava o marcador DURÁVEL de "este device já foi aprovado".
   *
   * <p>É ele que o boot SEM REDE consulta (`wasApprovedBefore`) pra entrar em modo offline e tocar
   * o cache. Precisa ser escrito em TODO caminho que conclui aprovação — não só no primeiro aceite.
   *
   * <p>Bug que originou o helper (2026-09-23, medido no Tab A7 e no embedded): a flag só era
   * escrita no fluxo de primeira aprovação. O caminho "servidor confirma que está aprovado" (boot
   * online) deixava o device aprovado apenas em memória, e os dois caminhos de revogação
   * (`recoverFromDeletedDevice` / 401 inesperado) apagavam a flag sem que ninguém a reescrevesse.
   * Resultado: reboot offline → `registrationStatus='idle'` → `initializeFromCache()` nunca roda →
   * "Aguardando conteúdo" com a programação e os vídeos inteiros em disco.
   */
  private persistirAprovacao(playerId: string | null | undefined): void {
    try {
      if (this.deviceId) {
        localStorage.setItem('efflux_device_id', this.deviceId);
      }
      localStorage.setItem('efflux_player_id', playerId || '');
      localStorage.setItem('efflux_device_approved', 'true');
    } catch {
      // localStorage indisponível (modo privado/quota) — best-effort, nunca quebra o boot.
    }
  }

  private recoverFromDeletedDevice(reason: string): void {
    console.log(
      `[AuthStore] Recovering from deleted device (${reason}) — reset to idle`,
    );
    localStorage.removeItem('efflux_player_id');
    localStorage.removeItem('efflux_device_approved');
    this.playerId = null;
    this.isAuthenticated = false;
    this.rejectionReason = null;
    this.registrationStatus = 'idle';
    this.stopPolling();
    this.disconnectSSE();
    // Auth scene observa registrationStatus === 'idle' + deviceId definido
    // → chama register() automaticamente no próximo render.
  }

  /**
   * Connect to SSE for real-time approval notifications.
   * Reconnects if connection was lost (e.g., after rejection).
   */
  private connectToSSE(): void {
    // If SSE is already connected and open, don't reconnect
    if (
      this.sseConnection &&
      this.sseConnection.readyState === EventSource.OPEN
    ) {
      console.log('[AuthStore] SSE already connected and open, skipping');
      return;
    }

    // If SSE exists but is closed/connecting, close it first to reconnect
    if (this.sseConnection) {
      console.log('[AuthStore] SSE exists but not open, reconnecting...');
      this.disconnectSSE();
    }

    const contentApiUrl = resolveApiBaseUrl();
    if (!contentApiUrl) {
      console.warn(
        '[AuthStore] No VITE_CONTENT_API_URL configured, cannot connect to SSE',
      );
      return;
    }

    // Check if EventSource is supported
    if (typeof EventSource === 'undefined') {
      console.warn(
        '[AuthStore] EventSource not supported on this platform, using polling only',
      );
      return;
    }

    try {
      // Use the correct device status stream endpoint
      const url = `${contentApiUrl}/api/devices/status/${encodeURIComponent(this.deviceId)}/stream`;
      console.log('[AuthStore] Connecting to device status SSE:', url);

      this.sseConnection = new EventSource(url);

      this.sseConnection.onopen = () => {
        console.log('[AuthStore] Device status SSE connection established');
      };

      this.sseConnection.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[AuthStore] SSE event received:', {
            raw: event.data,
            parsed: data,
            type: data.type,
            playerId: data.playerId,
            deviceId: data.deviceId,
          });

          if (data.type === 'device_approved') {
            // Device approved - authentication uses X-Device-Id header (no API key)
            // Handle approval even if currently in 'rejected' status (admin approved it)
            console.log('[AuthStore] Device approved via SSE!', {
              playerId: data.playerId,
              deviceId: data.deviceId,
              fullData: data,
              currentStatus: this.registrationStatus,
              currentIsAuthenticated: this.isAuthenticated,
            });
            // handleApproval already uses runInAction internally, no need to wrap it
            this.handleApproval(data.playerId);
          } else if (data.type === 'device_rejected') {
            console.log('[AuthStore] Device rejected via SSE:', data.reason);
            runInAction(() => {
              this.registrationStatus = 'rejected';
              this.rejectionReason = data.reason || 'Device rejected';
              // Don't stop polling - keep monitoring in case admin approves later
            });
          } else if (data.type === 'connected' || data.type === 'heartbeat') {
            // Server connection confirmation or heartbeat - just log
            console.log('[AuthStore] SSE:', data.type);
          }
        } catch (e) {
          console.warn('[AuthStore] SSE message parse error:', e);
        }
      };

      this.sseConnection.onerror = (error) => {
        console.warn(
          '[AuthStore] SSE connection error, falling back to polling only',
          error,
        );
        this.disconnectSSE();
        // Note: Polling continues as fallback
      };
    } catch (error) {
      console.warn('[AuthStore] SSE connection failed:', error);
    }
  }

  /**
   * Disconnect SSE connection.
   */
  private disconnectSSE(): void {
    if (this.sseConnection) {
      this.sseConnection.close();
      this.sseConnection = null;
    }
  }

  /**
   * Stop all polling and SSE connections.
   */
  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.disconnectSSE();
  }

  /**
   * Retry registration (e.g., after rejection or error).
   */
  async retryRegistration(): Promise<void> {
    runInAction(() => {
      this.registrationStatus = 'idle';
      this.error = null;
      this.rejectionReason = null;
    });

    await this.registerDevice();
  }

  /**
   * Handle de revocation server-side. Disparado quando um endpoint autenticado
   * retorna 401 inesperado (player foi deletado no admin / token revogado).
   * Limpa playerId local e força re-register limpo — device volta pra
   * pending_approval e admin precisa aprovar de novo.
   *
   * <p><b>Cooldown agressivo (2 min)</b>: evita storm caso múltiplos serviços
   * vejam 401 simultâneo (sse-token + sync + analytics todos batem 401 ao
   * mesmo tempo quando o token é revogado). Primeiro disparo wins, demais
   * são ignorados até o cooldown expirar.</p>
   *
   * <p><b>Preserva deviceId</b> (hardware ID) pra que o re-register encontre o
   * mesmo device no backend — só limpa playerId/approval. Logout completo é
   * deliberadamente evitado pra não atrapalhar polling de approval que está
   * pra começar.</p>
   *
   * <p>Renato 2026-06-12: cenário "tablet em campo + admin deletou player".</p>
   *
   * @param reason curto contexto pra logs (ex: 'sse-token 401')
   */
  async handleServerRevocation(reason: string): Promise<void> {
    const now = Date.now();
    const sinceLast = now - this.lastRevocationHandle;
    if (sinceLast < REVOCATION_COOLDOWN_MS) {
      console.log(
        `[AuthStore] revocation handling on cooldown (${Math.round(sinceLast / 1000)}s/${Math.round(REVOCATION_COOLDOWN_MS / 1000)}s) — ignoring "${reason}"`,
      );
      return;
    }
    this.lastRevocationHandle = now;

    console.warn(
      `[AuthStore] 🚨 Server revoked our credentials (${reason}) — clearing playerId and re-registering`,
    );

    this.stopPolling(); // Para SSE de approval antigo (vai reabrir após registro novo)

    runInAction(() => {
      this.isAuthenticated = false;
      this.playerId = null;
      this.registrationStatus = 'idle';
      this.error = null;
      this.rejectionReason = null;
    });

    localStorage.removeItem('efflux_player_id');
    localStorage.removeItem('efflux_device_approved');
    // NÃO limpa efflux_device_id (hardware-based) — preserva pra re-register
    // encontrar mesmo device no backend.

    await this.registerDevice();
  }

  /**
   * Logout - clear all authentication state.
   */
  logout(): void {
    this.stopPolling();

    runInAction(() => {
      this.isAuthenticated = false;
      this.playerId = null;
      this.registrationStatus = 'idle';
      this.error = null;
      this.rejectionReason = null;
    });

    localStorage.removeItem('efflux_player_id');
    localStorage.removeItem('efflux_device_api_key'); // Cleanup deprecated
    // Keep device_id - it's hardware-based

    console.log('[AuthStore] Logged out');
  }

  /**
   * Hard reset of device identity.
   * Use this when the device state is corrupted or rejected by server (403).
   */
  resetDeviceIdentity(): void {
    console.log('[AuthStore] 🧨 HARD RESET of device identity');

    runInAction(() => {
      this.isAuthenticated = false;
      this.playerId = null;
      this.deviceId = '';
      this.registrationStatus = 'idle';
    });

    // NOTE: We clear the AuthStore's copy ('efflux_device_id')
    // BUT we DO NOT clear the Platform Service's source of truth ('efflux_browser_hardware_id' etc.)
    // This ensures that when the page reloads, the SAME device ID is recovered.
    // The User Rule "Device ID must never change" is respected here.
    localStorage.removeItem('efflux_player_id');
    localStorage.removeItem('efflux_device_id');
    localStorage.removeItem('efflux_device_api_key');
    localStorage.removeItem('@efflux:token');

    // Force reload to ensure clean state
    window.location.reload();
  }

  /**
   * Refresh session - re-register device and verify approval status.
   * IMPORTANT: If device is pending approval, don't logout - just check status.
   */
  async refreshSession(): Promise<boolean> {
    console.log('[AuthStore] Refreshing session...', {
      currentStatus: this.registrationStatus,
      isAuthenticated: this.isAuthenticated,
      deviceId: this.deviceId,
    });

    // If device is pending approval, don't logout - just check status via polling/SSE
    if (this.registrationStatus === 'pending_approval' && this.deviceId) {
      console.log(
        '[AuthStore] Device is pending approval, resuming polling/SSE instead of re-registering',
      );
      this.startPollingForApproval();
      return false; // Still not authenticated
    }

    // If device was authenticated but sync failed, try to verify status
    // Don't logout immediately - check status first
    if (this.isAuthenticated && this.deviceId) {
      console.log(
        '[AuthStore] Device was authenticated, checking status without logout',
      );
      // Try to check status via status endpoint
      try {
        const contentApiUrl = resolveApiBaseUrl();
        const response = await axios.get<RegistrationResponse>(
          `${contentApiUrl}${STATUS_CHECK_ENDPOINT}/${this.deviceId}`,
          { timeout: 5000 },
        );

        if (response.data.status === 'approved') {
          console.log('[AuthStore] Device still approved, session is valid');
          return true;
        } else {
          console.log(
            '[AuthStore] Device is no longer approved, status:',
            response.data.status,
          );
          // Device was rejected or is pending - logout and re-register
          this.logout();
          await this.registerDevice();
          return this.isAuthenticated;
        }
      } catch (error: unknown) {
        // If 404, device might not be registered yet - don't logout immediately
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          console.log(
            '[AuthStore] Device not found (404) - might be pending registration',
          );
          // If we have deviceId but device not found, mark as pending and start polling
          if (this.deviceId) {
            runInAction(() => {
              this.registrationStatus = 'pending_approval';
              this.isAuthenticated = false;
            });
            this.startPollingForApproval();
          } else {
            // No deviceId, need to register
            this.logout();
            await this.registerDevice();
          }
          return false;
        }
        console.warn(
          '[AuthStore] Failed to check status, will logout and re-register',
          error instanceof Error ? error.message : error,
        );
        this.logout();
        await this.registerDevice();
        return this.isAuthenticated;
      }
    }

    // If not authenticated and not pending, logout and register
    this.logout();
    await this.registerDevice();

    return this.isAuthenticated;
  }

  // ===========================================================================
  // COMPUTED / GETTERS
  // ===========================================================================

  get isPending(): boolean {
    return this.registrationStatus === 'pending_approval';
  }

  get hasError(): boolean {
    return (
      this.registrationStatus === 'error' ||
      this.registrationStatus === 'rejected'
    );
  }

  get tenantId(): string {
    return import.meta.env.VITE_TENANT_ID || '';
  }

  get tenantSlug(): string {
    // Renato 2026-06-13: fallback 'efflux' (não 'default') — vide comentário
    // em registerDevice. Mantém paridade com o fallback usado no registro.
    return resolveTenantSlug();
  }

  get tenantName(): string {
    return import.meta.env.VITE_TENANT_NAME || 'Efflux';
  }

  // ===========================================================================
  // ORIENTATION METHODS
  // ===========================================================================

  /**
   * Set selected orientation override.
   * Pass null to use auto-detected orientation.
   */
  setSelectedOrientation(orientation: ScreenOrientation | null): void {
    this.selectedOrientation = orientation;
    if (orientation) {
      localStorage.setItem(ORIENTATION_STORAGE_KEY, orientation);
    } else {
      localStorage.removeItem(ORIENTATION_STORAGE_KEY);
    }
  }

  /**
   * Update orientation on the server.
   * Works for both pending and approved devices.
   * For approved devices, server will trigger SSE content_changed to force re-sync.
   */
  async updateOrientation(orientation: ScreenOrientation): Promise<boolean> {
    // Skip update for rejected devices
    if (this.registrationStatus === 'rejected') {
      console.log(
        '[AuthStore] Skipping orientation update - device is rejected',
      );
      return false;
    }

    if (!this.deviceId) {
      console.warn('[AuthStore] Cannot update orientation - no device ID');
      return false;
    }

    const contentApiUrl = resolveApiBaseUrl();

    try {
      console.log(
        '[AuthStore] Updating orientation on server:',
        orientation,
        'status:',
        this.registrationStatus,
      );

      await axios.patch(
        `${contentApiUrl}/api/devices/${encodeURIComponent(this.deviceId)}/orientation`,
        { orientation },
        {
          timeout: 10000,
          headers: { 'Content-Type': 'application/json' },
        },
      );

      runInAction(() => {
        this.setSelectedOrientation(orientation);
      });

      console.log('[AuthStore] Orientation updated on server:', orientation);
      return true;
    } catch (error: unknown) {
      console.error(
        '[AuthStore] Failed to update orientation:',
        error instanceof Error ? error.message : error,
      );
      return false;
    }
  }

  /**
   * Update orientation with a custom orientacaoId.
   * Sends the custom orientation ID to the server, which sets the player's
   * orientacaoId and derives the standard orientation from dimensions.
   */
  async updateOrientationWithId(orientacaoId: number): Promise<void> {
    if (this.registrationStatus === 'rejected') {
      console.log(
        '[AuthStore] Skipping custom orientation update - device is rejected',
      );
      return;
    }

    if (!this.deviceId) {
      console.warn('[AuthStore] Cannot update orientation - no device ID');
      return;
    }

    const contentApiUrl = resolveApiBaseUrl();

    try {
      console.log(
        '[AuthStore] Updating custom orientation on server: orientacaoId=',
        orientacaoId,
      );

      await axios.patch(
        `${contentApiUrl}/api/devices/${encodeURIComponent(this.deviceId)}/orientation`,
        { orientacao_id: orientacaoId },
        {
          timeout: 10000,
          headers: { 'Content-Type': 'application/json' },
        },
      );

      console.log(
        '[AuthStore] Custom orientation updated on server: orientacaoId=',
        orientacaoId,
      );
    } catch (error: unknown) {
      console.error(
        '[AuthStore] Failed to update custom orientation:',
        error instanceof Error ? error.message : error,
      );
    }
  }

  /**
   * Get the effective orientation to send to server.
   * Uses override if set, otherwise uses platform's detected orientation.
   */
  getEffectiveOrientation(): ScreenOrientation {
    if (this.selectedOrientation) {
      return this.selectedOrientation;
    }
    // Use platform service if available, fallback to 'landscape'
    if (this.platformService) {
      return this.platformService.getOrientation();
    }
    return 'landscape';
  }
}

export default AuthStore;
